function getActualSourceHandle(edge) {
  return edge?.data?._originalSourceHandle || edge?.sourceHandle || null;
}

function getActualTargetHandle(edge) {
  return edge?.data?._prev || edge?.targetHandle || null;
}

function collectValidHandlesByType(nodes) {
  const validSource = new Set();
  const validTarget = new Set();

  for (const node of nodes) {
    if (node.type !== 'sourceNode' && node.type !== 'destinationNode') continue;
    const groups = node?.data?.subFields || [];
    for (const g of groups) {
      const kids = g?.children || [];
      for (const f of kids) {
        if (!f?.id) continue;
        if (node.type === 'sourceNode') validSource.add(f.id);
        if (node.type === 'destinationNode') validTarget.add(f.id);
      }
    }
  }
  return { validSource, validTarget };
}

// --- keep existing helpers: getActualSourceHandle / getActualTargetHandle ---

function syncFieldFlagsWithEdges(nodes, edges) {
  if (!Array.isArray(nodes)) return nodes || [];
  const liveSource = new Set(
    edges.flatMap(e => [e.sourceHandle, e?.data?._originalSourceHandle]).filter(Boolean)
  );
  const liveTarget = new Set(
    edges.flatMap(e => [e.targetHandle, e?.data?._prev]).filter(Boolean)
  );

  return nodes.map(node => {
    const sub = node?.data?.subFields;
    if (!sub) return node;

    const isSource = node.type === 'sourceNode';
    const liveSet = isSource ? liveSource : liveTarget;

    const nextSub = sub.map(group => ({
      ...group,
      children: (group.children || []).map(field => {
        const stillLive = liveSet.has(field.id);
        if (stillLive) return { ...field, isMapped: true };
        const clone = { ...field };
        delete clone.color;
        return { ...clone, isMapped: false, isMismatch: false };
      })
    }));

    return { ...node, data: { ...node.data, subFields: nextSub } };
  });
}


export function pruneInvalidEdgesAndFunctionChains({ nodes, edges }) {
  if (!Array.isArray(nodes) || !Array.isArray(edges)) {
    return { nodes: nodes || [], edges: edges || [] };
  }

  const { validSource, validTarget } = collectValidHandlesByType(nodes);

  let prunedEdges = edges.filter((e) => {
    const isDirectFromSourceField = nodes.find((n) => n.id === e.source)?.type === 'sourceNode';
    const isDirectToDestField    = nodes.find((n) => n.id === e.target)?.type === 'destinationNode';

    const srcHandle = getActualSourceHandle(e);
    const tgtHandle = getActualTargetHandle(e);

    if (isDirectFromSourceField && srcHandle && !validSource.has(srcHandle)) {
      return false; 
    }
    if (isDirectToDestField && tgtHandle && !validTarget.has(tgtHandle)) {
      return false; 
    }
    return true;
  });

  let prunedNodes = nodes.slice();
  let changed = true;

  while (changed) {
    changed = false;

    const inDegree = new Map();
    const outDegree = new Map();
    for (const n of prunedNodes) {
      inDegree.set(n.id, 0);
      outDegree.set(n.id, 0);
    }
    for (const e of prunedEdges) {
      outDegree.set(e.source, (outDegree.get(e.source) || 0) + 1);
      inDegree.set(e.target, (inDegree.get(e.target) || 0) + 1);
    }

    const danglingFnIds = new Set(
      prunedNodes
        .filter((n) => n.type === 'functionNode')
        .filter((fn) => (inDegree.get(fn.id) || 0) === 0 || (outDegree.get(fn.id) || 0) === 0)
        .map((fn) => fn.id)
    );

    if (danglingFnIds.size > 0) {
      prunedNodes = prunedNodes.filter((n) => !danglingFnIds.has(n.id));
      const beforeCount = prunedEdges.length;
      prunedEdges = prunedEdges.filter((e) => !danglingFnIds.has(e.source) && !danglingFnIds.has(e.target));
      changed = changed || prunedEdges.length !== beforeCount;
    }
  }

  return { nodes: prunedNodes, edges: prunedEdges };
}

export function pruneAndSyncInvalidEdges({ nodes, edges }) {
  const { nodes: prunedNodes, edges: prunedEdges } =
    pruneInvalidEdgesAndFunctionChains({ nodes, edges });

  const syncedNodes = syncFieldFlagsWithEdges(prunedNodes, prunedEdges);
  return { nodes: syncedNodes, edges: prunedEdges };
}
